import { Fragment, useCallback, useContext, useEffect, useState } from "react";

import { Booking, api, IBookingSearch, RequestStatus, IBookingAdvancedSearch } from "@/logic/api";

import BookingListItem from "@/components/lists/listItems/BookingListItem/BookingListItem";
import Scaler from "../../../scaler/Scaler";
import { SearchContext } from "../../searchBooking";

import NotFoundPageWithComponent from "@/pages/error/NotFoundPageWithComponent";
import { PagedDataView } from "@/components/lists/PagedDataView/PagedDataView";
import { useReleaseManagement, ReleaseFlag } from "@/utility/feature-management";
import AdvancedSearchOptionsAccordion from "./AdvancedSearchOptionsAccordion";
import { useTranslation } from "react-i18next";
import { Box, Typography, CircularProgress } from "@mui/material";
import SearchFilter, { SearchFilters } from "./SearchFilters";

import styles from "./styles";

const SearchResults = () => {
    const { t } = useTranslation();

    const isBookingAdvancedSearchReleased = useReleaseManagement(ReleaseFlag.releaseBookingAdvancedSearch);
    const isBookingOpenInNewTabReleased = useReleaseManagement(ReleaseFlag.releaseBookingSearchOpenInNewTab);
    const [expandSearch, setExpandSearch] = useState(false);
    const [partialMatch, setPartialMatch] = useState(false);

    const selectSearchResult = () => {
        if (!isBookingOpenInNewTabReleased) {
            if (searchContext.setCriteria) {
                if (searchContext.criteria) {
                    searchContext.setCriteria({ ...searchContext.criteria, show: false });
                }
            }
        }
    };

    const searchContext = useContext(SearchContext);

    const updateSearch = (searchFilters: SearchFilters) => {
        setExpandSearch(searchFilters.expandSearchCriteria);
        setPartialMatch(searchFilters.partialMatchCriteria);
    }

    useEffect(() => {
        if (!searchContext.criteria?.show || isBookingAdvancedSearchReleased) {
            return;
        }

        const handler = setTimeout(() => {
            const searchParams = {
                search: searchContext.criteria.search,
                cancelled: expandSearch,
                exact: !partialMatch,
                show: true,
                expandedSearch: expandSearch,
            };

            searchContext.setCriteria({
                ...searchParams,
                show: true
            });
        }, 500);

        return () => {
            clearTimeout(handler);
        }
    }, [expandSearch, partialMatch, isBookingAdvancedSearchReleased])

    const triggerAdvancedSearch = useCallback((filters: IBookingAdvancedSearch) => {
        if (isBookingAdvancedSearchReleased && searchContext.criteria?.show) {
            const searchParams = {
                search: searchContext.criteria.search,
                cancelled: false,
                exact: false,
                show: true,
                expandedSearch: false,
                advancedSearch: filters
            };

            searchContext.setCriteria({
                ...searchParams,
                show: true
            });
        }
    }, [isBookingAdvancedSearchReleased, searchContext]);

    if (searchContext.criteria == null) {
        return <Fragment />
    }

    if (searchContext.criteria.search.length == 0) {
        return <Fragment />
    }

    if (searchContext.criteria.show == false) {
        return <Fragment />
    }

    return <div style={styles.wrapper}>
        <div style={styles.container}>
            <div style={styles.padded}>
                <PagedDataView<IBookingSearch, Booking>
                    apiHook={api.hooks.booking.searchBooking}
                    parameters={searchContext.criteria}
                    header={(hook, records) => {

                        // Loading screen
                        if (isBookingAdvancedSearchReleased && hook.status === RequestStatus.loading) {
                            return <Box sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                py: 8,
                                gap: 2
                            }}>
                                <CircularProgress
                                    size={40}
                                    sx={{ color: 'brand.main' }}
                                />
                                <Typography variant="body1" color="text.secondary">
                                    {t("general.loading")}...
                                </Typography>
                            </Box>;
                        }

                        // No results screen (legacy)
                        if (!isBookingAdvancedSearchReleased) {
                            if (records.length == 0 && hook.status == RequestStatus.loaded) {
                                return <NotFoundPageWithComponent wrapWithDash={false} title={t("errors.searchNotFound2.title")} message={t("errors.searchNotFound2.message")}><div style={{ marginTop: 20 }}><SearchFilter defaultExpandSearch={searchContext.criteria.reload ? false : expandSearch} defaultPartialMatch={searchContext.criteria.reload ? false : partialMatch} updateSearch={updateSearch} /></div></NotFoundPageWithComponent>;
                            }
                        }
                        if (searchContext.criteria != null) {
                            return <div style={styles.toolBar}>
                                {isBookingAdvancedSearchReleased ? (
                                    <>
                                        {
                                            hook.status === RequestStatus.loaded && Array.isArray(hook.data) && hook.data.length === 0 ? (
                                                <Box sx={{ textAlign: 'center', py: 6 }}>
                                                    <Typography
                                                        variant="h2"
                                                        sx={{
                                                            fontWeight: "bold",
                                                            color: (theme) => theme.palette.brand.main,
                                                        }}
                                                    >
                                                        {t("errors.searchNotFound2.title")}
                                                    </Typography>
                                                    <Typography
                                                        variant="h4"
                                                        sx={{ color: 'text.primary', fontWeight: 600, maxWidth: 1100, mx: 'auto' }}
                                                    >
                                                        {t("errors.searchNotFound2.message")}
                                                    </Typography>
                                                </Box>
                                            ) : (
                                                <Scaler>
                                                    <Typography variant="h1" sx={{ ...styles.title, marginY: 7 }}>{t("home.search.searchResults", { searchText: `'${searchContext.criteria.search}'` })}</Typography>
                                                </Scaler>
                                            )
                                        }
                                        <AdvancedSearchOptionsAccordion
                                            hasResults={records.length > 0}
                                            expanded={records.length === 0 && hook.status === RequestStatus.loaded}
                                            onApplyFilters={triggerAdvancedSearch}
                                            searchText={searchContext.criteria.search}
                                            isReload={searchContext.criteria.reload}
                                        />
                                    </>
                                ) : (
                                    <Scaler>
                                        <h1 style={styles.title}>{t("home.search.searchResults", { searchText: searchContext.criteria.search })}</h1>
                                        <SearchFilter defaultExpandSearch={expandSearch && !searchContext.criteria.reload} defaultPartialMatch={partialMatch && !searchContext.criteria.reload} updateSearch={updateSearch} />
                                    </Scaler>
                                )}
                            </div>
                        }

                        return <Fragment></Fragment>
                    }}
                    renderItem={(item) => {
                        return <BookingListItem
                            booking={item}
                            onClick={!isBookingOpenInNewTabReleased ? () => selectSearchResult() : undefined}
                            openInNewTab={isBookingOpenInNewTabReleased}
                        />
                    }}
                />
            </div>
        </div>
    </div>

}

export default SearchResults;
